import { Box, Button, TextField } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedTime, IntlShape } from 'react-intl';
import { ReactElement, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { DocumentComment } from '../../../common/models/document-comment';
import { PoolTimelineItem } from '../../../common/components/timeline/models/pool-timeline-item';

const c = tokens.color;

export interface CommentTimelineContext {
    intl: IntlShape;
    onUpdate: (commentId: number, text: string) => Promise<void>;
    onDelete: (commentId: number) => Promise<void>;
}

interface StreamCommentProps {
    comment: DocumentComment;
    onUpdate: (commentId: number, text: string) => Promise<void>;
    onDelete: (commentId: number) => Promise<void>;
}

/**
 * The remark itself, with the two actions its author has. Editing happens in
 * place: the entry keeps its position in the stream, which is where the reader
 * is looking.
 */
const StreamComment = ({ comment, onUpdate, onDelete }: StreamCommentProps): ReactElement => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.text);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (!text.trim() || text === comment.text) {
            setEditing(false);
            setText(comment.text);
            return;
        }

        setBusy(true);
        await onUpdate(comment.id, text.trim());
        setBusy(false);
        setEditing(false);
    };

    if (editing) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '4px' }}>
                <TextField
                    value={text}
                    onChange={event => setText(event.target.value)}
                    // Autofocus alone leaves the caret in front of the existing
                    // text, so typing would prepend to what was written.
                    onFocus={event => event.target.setSelectionRange(text.length, text.length)}
                    multiline={true}
                    minRows={2}
                    size="small"
                    autoFocus={true}
                    fullWidth={true}
                />
                <Box sx={{ display: 'flex', gap: '6px' }}>
                    <Button size="small" variant="contained" disabled={busy} onClick={save}>
                        <FormattedMessage id="app.common.save" />
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => {
                            setText(comment.text);
                            setEditing(false);
                        }}
                    >
                        <FormattedMessage id="app.common.cancel" />
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ whiteSpace: 'pre-wrap', color: c.ink }}>{comment.text}</Box>
            {comment.editable && (
                <Box sx={{ display: 'flex', gap: '9px', paddingTop: '3px' }}>
                    <Box
                        component="span"
                        role="button"
                        onClick={() => setEditing(true)}
                        sx={{ fontSize: '10.5px', color: c.accent, cursor: 'pointer' }}
                    >
                        <FormattedMessage id="app.common.edit" />
                    </Box>
                    <Box
                        component="span"
                        role="button"
                        onClick={() => onDelete(comment.id)}
                        sx={{ fontSize: '10.5px', color: c.crit, cursor: 'pointer' }}
                    >
                        <FormattedMessage id="app.common.delete" />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export const createCommentTimelineItem = (
    comment: DocumentComment,
    context: CommentTimelineContext
): PoolTimelineItem => ({
    id: comment.id,
    key: `comment-${comment.id}`,
    timestamp: comment.createdAt,
    variant: 'ok',
    icon: 'icon-comment',
    header: <FormattedMessage id="app.stream.comment.header" />,
    actor: comment.createdBy?.fullName,
    subheader: (
        <span>
            {comment.createdBy && comment.createdBy.fullName + ' '}
            <FormattedMessage id="app.activity.createdAt" /> <FormattedDate value={comment.createdAt} />
            &nbsp;
            <FormattedTime value={comment.createdAt} />
            {comment.updatedAt !== comment.createdAt && (
                <>
                    {' · '}
                    <FormattedMessage id="app.activity.updated" /> <FormattedDate value={comment.updatedAt} />
                    &nbsp;
                    <FormattedTime value={comment.updatedAt} />
                </>
            )}
        </span>
    ),
    footer: undefined,
    content: <StreamComment comment={comment} onUpdate={context.onUpdate} onDelete={context.onDelete} />,
    bodyOnly: true,
    commentsDisabled: true,
    comments: [],
    changes: [],
});
